import type { AnchorHTMLAttributes, ComponentType, ImgHTMLAttributes, ReactNode } from 'react';
import { createContext, useContext } from 'react';

export type PlatformImageProps = ImgHTMLAttributes<HTMLImageElement> & {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  fill?: boolean;
  className?: string;
  unoptimized?: boolean;
};

// UrlObject-like type compatible with Next.js's Url type
export type PlatformUrlObject = {
  pathname?: string | null;
  query?: string | null | Record<string, string | number | boolean | readonly string[] | readonly number[] | readonly boolean[] | null | undefined>;
  hash?: string | null;
  href?: string | null;
  protocol?: string | null;
  auth?: string | null;
  hostname?: string | null;
  port?: string | number | null;
  host?: string | null;
  search?: string | null;
  slashes?: boolean | null;
};

export type PlatformLinkProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> & {
  href: string | PlatformUrlObject;
  children: ReactNode;
  // Next.js-specific (ignored by pure implementation)
  as?: string;
  replace?: boolean;
  scroll?: boolean;
  shallow?: boolean;
  prefetch?: boolean | null;
  locale?: string | false;
};

export interface PlatformComponents {
  Image: ComponentType<PlatformImageProps>;
  Link: ComponentType<PlatformLinkProps>;
}

/**
 * Optional CDN/image-proxy config for the platform. Supplied by hosts (e.g. Next.js) that
 * route images through a resizing proxy so that shared components rendering remote images
 * (e.g. the TipTap `/` content dropdown) produce CSP-safe, allowlisted URLs instead of
 * hitting arbitrary remote hosts directly. Absent on extension/electron → no proxying.
 */
export type PlatformImageCdn = {
  /** Image resizing server base URL (e.g. https://assets.useposeidon.com). */
  cdnBaseUrl?: string;
  /** Backend URL used to resolve relative image paths. */
  backendUrl?: string;
};

// Pure HTML defaults (NO Next.js imports)
const ImagePure = ({ src, alt, width, height, fill, className, style, unoptimized: _, ...props }: PlatformImageProps) => {
  const fillStyle = fill
    ? { position: 'absolute' as const, inset: 0, width: '100%', height: '100%', objectFit: 'cover' as const }
    : undefined;
  return (
    <img
      src={src}
      alt={alt}
      width={fill ? undefined : width}
      height={fill ? undefined : height}
      className={className}
      style={{ ...fillStyle, ...style }}
      {...props}
    />
  );
};

const LinkPure = ({
  href,
  children,
  as: _as,
  replace: _replace,
  scroll: _scroll,
  shallow: _shallow,
  prefetch: _prefetch,
  locale: _locale,
  ...props
}: PlatformLinkProps) => {
  const hrefString = typeof href === 'string' ? href : (href.pathname ?? href.href ?? '#');
  return (
    <a href={hrefString} {...props}>
      {children}
    </a>
  );
};

const defaultComponents: PlatformComponents = {
  Image: ImagePure,
  Link: LinkPure,
};

const PlatformContext = createContext<PlatformComponents>(defaultComponents);
const PlatformImageCdnContext = createContext<PlatformImageCdn | undefined>(undefined);

export type PlatformProviderProps = {
  components?: Partial<PlatformComponents>;
  imageCdn?: PlatformImageCdn;
  children: ReactNode;
};

export const PlatformProvider = ({ components, imageCdn, children }: PlatformProviderProps) => (
  <PlatformContext.Provider value={{ ...defaultComponents, ...components }}>
    <PlatformImageCdnContext.Provider value={imageCdn}>{children}</PlatformImageCdnContext.Provider>
  </PlatformContext.Provider>
);

export const usePlatformComponents = () => useContext(PlatformContext);
export const usePlatformImage = () => usePlatformComponents().Image;
export const usePlatformLink = () => usePlatformComponents().Link;
export const usePlatformImageCdn = () => useContext(PlatformImageCdnContext);
